import type { ComponentType, SVGProps } from 'react';
import {
    ArrowPathIcon,
    ArrowsRightLeftIcon,
    BoltIcon,
    CircleStackIcon,
    LinkIcon,
    LinkSlashIcon
} from '@heroicons/react/24/outline';
import { Tooltip } from '@odo/shared-ui';
import { useElementWidth } from '../../../../hooks/useElementWidth';
import {
    ButtonSpinner,
    DESTRUCTIVE_BUTTON_CLASS,
    NEUTRAL_BUTTON_CLASS
} from './CredsActionButtons';
import type { IteroBoxAction } from './iteroBoxActions';

interface ActionUi {
    label: string;
    icon: ComponentType<SVGProps<SVGSVGElement>>;
    destructive?: boolean;
}

const ACTION_UI: Readonly<Record<IteroBoxAction, ActionUi>> = {
    browse: { label: 'Browse iTero data', icon: CircleStackIcon },
    test: { label: 'Test', icon: BoltIcon },
    connect: { label: 'Connect', icon: LinkIcon },
    chooseClinic: { label: 'Choose clinic', icon: ArrowsRightLeftIcon },
    changeClinic: { label: 'Change clinic', icon: ArrowsRightLeftIcon },
    refresh: { label: 'Refresh', icon: ArrowPathIcon },
    disconnect: { label: 'Disconnect', icon: LinkSlashIcon, destructive: true }
};

const RETRY_UI: ActionUi = { label: 'Retry', icon: ArrowPathIcon };

// Disconnect, the widest label that can't wrap, is 108px; a narrower row shows icons with tooltips.
const LABELLED_ROW_MIN_WIDTH = 128;

// Labelled buttons wrap as whole units; only one wider than the whole box wraps its label.
const LABELLED_CLASS = 'max-w-full !h-auto min-h-7 py-1 text-left';
const ICON_ONLY_CLASS = 'w-7 shrink-0 justify-center !px-0';
// ml-auto sets the destructive action apart at the end of whichever line it lands on.
const DESTRUCTIVE_EXTRA_CLASS = 'ml-auto';

function rowButtonClass(destructive: boolean, iconOnly: boolean): string {
    const layout = iconOnly ? ICON_ONLY_CLASS : LABELLED_CLASS;
    return destructive
        ? `${DESTRUCTIVE_BUTTON_CLASS} ${layout} ${DESTRUCTIVE_EXTRA_CLASS}`
        : `${NEUTRAL_BUTTON_CLASS} ${layout}`;
}

interface RowButtonProps {
    ui: ActionUi;
    iconOnly: boolean;
    pending: boolean;
    disabled: boolean;
    onClick: () => void;
}

function RowButton({ ui, iconOnly, pending, disabled, onClick }: RowButtonProps) {
    const { label, icon: Icon, destructive = false } = ui;
    return (
        <Tooltip content={label} disabled={!iconOnly}>
            <button
                type="button"
                onClick={onClick}
                disabled={disabled}
                aria-label={iconOnly ? label : undefined}
                className={rowButtonClass(destructive, iconOnly)}
            >
                <span className="inline-flex shrink-0">
                    {pending ? (
                        <ButtonSpinner />
                    ) : (
                        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                    )}
                </span>
                {!iconOnly && label}
            </button>
        </Tooltip>
    );
}

interface IteroActionButtonsProps {
    actions: readonly IteroBoxAction[];
    disabled: boolean;
    pending: readonly IteroBoxAction[];
    onAction: (action: IteroBoxAction) => void;
    onRetry?: () => void;
}

export function IteroActionButtons({
    actions,
    disabled,
    pending,
    onAction,
    onRetry
}: IteroActionButtonsProps) {
    const { ref, width } = useElementWidth<HTMLDivElement>();
    const iconOnly = width > 0 && width < LABELLED_ROW_MIN_WIDTH;
    if (actions.length === 0 && !onRetry) return null;

    return (
        <div ref={ref} className="flex flex-wrap items-center gap-2">
            {onRetry && (
                <RowButton
                    ui={RETRY_UI}
                    iconOnly={iconOnly}
                    pending={false}
                    disabled={disabled}
                    onClick={onRetry}
                />
            )}
            {actions.map((action) => (
                <RowButton
                    key={action}
                    ui={ACTION_UI[action]}
                    iconOnly={iconOnly}
                    pending={pending.includes(action)}
                    disabled={disabled}
                    onClick={() => onAction(action)}
                />
            ))}
        </div>
    );
}
